import { useToast } from "@/components/ui/use-toast";
import { fetchApi } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const conversationBuckets = [
  "needs_response",
  "online",
  "low_engagement",
  "new",
] as const;

export type ConversationBucket = (typeof conversationBuckets)[number];

export interface TrainingInitialMessage {
  sender_type: "mentor" | "simulated_teen";
  content: string;
}

// Profile keys owned by the structured editor form. Anything else in the
// profile blob is passed through as free-form "extra" JSON.
export const managedProfileKeys = [
  "interests",
  "struggles",
  "proudMoments",
  "vibe",
  "closestPerson",
  "summary",
  "initial_messages",
] as const;

export type ManagedProfileKey = (typeof managedProfileKeys)[number];

interface ManagedProfileFields {
  interests?: string[];
  struggles?: string[];
  proudMoments?: string[];
  vibe?: string | null;
  closestPerson?: string | null;
  summary?: string | null;
  initial_messages?: TrainingInitialMessage[];
}

// Compile-time guard: adding a field to ManagedProfileFields without listing
// it in managedProfileKeys (or vice versa) fails to build. Without this, an
// unlisted field would surface in the "extra JSON" editor and be silently
// overwritten by the form on save.
type MissingFromKeys = Exclude<keyof ManagedProfileFields, ManagedProfileKey>;
type MissingFromFields = Exclude<ManagedProfileKey, keyof ManagedProfileFields>;
const _profileKeysCoverFields: MissingFromKeys extends never ? true : never =
  true;
const _fieldsCoverProfileKeys: MissingFromFields extends never ? true : never =
  true;
void _profileKeysCoverFields;
void _fieldsCoverProfileKeys;

export type TrainingTeenProfile = ManagedProfileFields & {
  [key: string]: unknown;
};

export interface TrainingContentTeen {
  id: string;
  level_id: string;
  position: number;
  display_name: string;
  age: number;
  gender: string | null;
  pronouns: string | null;
  profile: TrainingTeenProfile;
  persona_prompt: string | null;
  conversation_bucket: ConversationBucket;
  updated_at: string;
}

export interface TrainingContentLevel {
  id: string;
  module_id: string;
  position: number;
  title: string;
  summary: string | null;
  teens: TrainingContentTeen[];
}

export interface TrainingContentModule {
  id: string;
  position: number;
  title: string;
  description: string | null;
  levels: TrainingContentLevel[];
}

export interface TrainingContentResponse {
  modules: TrainingContentModule[];
}

// The editor always sends the full record, so every field is required — a
// partial payload here would silently rely on unspecified server-side PATCH
// semantics.
export interface UpdateTrainingTeenPayload {
  display_name: string;
  age: number;
  gender: string | null;
  pronouns: string | null;
  persona_prompt: string | null;
  conversation_bucket: ConversationBucket;
  profile: TrainingTeenProfile;
}

export interface TrainingPlaygroundTranscriptMessage {
  sender: "mentor" | "simulated_teen";
  content: string;
}

export interface TrainingPlaygroundReplyResponse {
  reply: TrainingPlaygroundTranscriptMessage;
  metadata: Record<string, unknown>;
}

export const trainingSimulatorContentQueryKey = [
  "training-simulator-content",
  "teens",
] as const;

export const useTrainingTeenContent = (enabled = true) =>
  useQuery({
    queryKey: trainingSimulatorContentQueryKey,
    queryFn: async () => {
      const { data } = await fetchApi<TrainingContentResponse>({
        url: "v2/training-simulator/content/teens",
        method: "GET",
      });

      return data;
    },
    enabled,
  });

export const useUpdateTrainingTeenContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["training-simulator-content", "update-teen"],
    mutationFn: async ({
      teenId,
      payload,
    }: {
      teenId: string;
      payload: UpdateTrainingTeenPayload;
    }) => {
      const { data } = await fetchApi<{ teen: TrainingContentTeen }>({
        url: `v2/training-simulator/content/teens/${teenId}`,
        method: "PATCH",
        data: payload,
      });

      return data.teen;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trainingSimulatorContentQueryKey,
      });
      toast({
        title: "Teen profile updated",
        description: "Training simulator content has been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update teen profile",
        description: "Please check the fields and try again.",
        variant: "destructive",
      });
    },
  });
};

export const useGenerateTrainingTeenPlaygroundReply = () => {
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["training-simulator-content", "playground-reply"],
    mutationFn: async ({
      teenId,
      transcript,
    }: {
      teenId: string;
      transcript: TrainingPlaygroundTranscriptMessage[];
    }) => {
      const { data } = await fetchApi<TrainingPlaygroundReplyResponse>({
        url: `v2/training-simulator/content/teens/${teenId}/playground/reply`,
        method: "POST",
        data: { transcript },
      });

      return data;
    },
    onError: () => {
      toast({
        title: "Failed to generate reply",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};
